/**
 * dscFilteringData.js
 *
 * Builds the canonical master lists (`dscFilteringData` + `dscDTHHammerData`)
 * that the DSC cascade reducer consumes when computing dropdown options.
 *
 * PORTED FROM: `searchResults.js:1043-1172` `manageDSCData()`.
 *   - TH branch (lines 1046-1082): builds 3 base slot cards with the
 *     manufacturer dropdown seeded from `dscFilteringData[].manufacture`.
 *   - DTH branch (lines 1083-1160): builds 3 base slot cards plus the
 *     `dscDTHHammerData` aggregated master list for hammer-family options.
 *
 * @module services/dscFilteringData
 * @since 2026-05-20 (Group E SP1.3)
 */

import { getFilteringData, getDTHHammerData } from './dscMatchingService';
import { hammerFamilyRank } from '@data/dscSlotConfig';

// ============================================================================
// Public API
// ============================================================================

export function buildDSCFilteringData(productLine, products, opts = {}) {
  const { entityCode = null } = opts;
  if (productLine === 'TH') {
    return {
      filteringData: getFilteringData('top-hammer', products, { entityCode }),
      dthHammerData: [],
    };
  }
  if (productLine === 'DTH') {
    return {
      filteringData: getFilteringData('down-the-hole', products, { entityCode }),
      dthHammerData: getDTHHammerData(products, { entityCode }),
    };
  }
  return { filteringData: [], dthHammerData: [] };
}

export function buildInitialSlotOptions(productLine, products, opts = {}) {
  const { unitImperial = false } = opts;

  if (productLine === 'TH') {
    const { filteringData } = buildDSCFilteringData('TH', products, opts);
    const mfrs = new Set();
    for (const item of filteringData) {
      if (item.manufacture) mfrs.add(item.manufacture);
    }
    const mfrOptions = [...mfrs]
      .map((m) => ({ label: m, value: m }))
      .sort((a, b) => a.label.localeCompare(b.label));
    return {
      'shank-adapter': [{ label: 'Select', value: 'None' }, ...mfrOptions],
      rod: [],
      'drill-bit': [],
    };
  }

  if (productLine === 'DTH') {
    const { filteringData, dthHammerData } = buildDSCFilteringData('DTH', products, opts);

    // dthBitOptions sourced from unique bitDiameter values in dscFilteringData.
    const bitOptionsMap = new Map();
    for (const item of filteringData) {
      const value = item.bitDiameter;
      const label = !unitImperial ? item.bitDiameterMM : item.bitDiameterInch;
      if (value != null && label != null && !bitOptionsMap.has(value)) {
        bitOptionsMap.set(value, label);
      }
    }
    const dthBitOptions = [
      { label: 'Select', value: 'None' },
      ...[...bitOptionsMap.entries()]
        .map(([value, label]) => ({ label: String(label), value: String(value) }))
        .sort((a, b) => {
          const an = parseFloat(a.label);
          const bn = parseFloat(b.label);
          if (Number.isFinite(an) && Number.isFinite(bn)) return an - bn;
          return String(a.label).localeCompare(String(b.label));
        }),
    ];

    // Hammer-family options (label = `hammerFamily-hammerSizeInch`).
    // RH570 surfaces above RH560 via hammerFamilyRank.
    const familyMap = new Map();
    for (const item of dthHammerData) {
      const family = item.hammerFamily;
      const size = item.hammerSizeInch;
      if (family && size) {
        const label = `${family}-${size}`;
        if (!familyMap.has(label)) familyMap.set(label, label);
      }
    }
    const dthHammerFamilyOptions = [
      { label: 'Select', value: 'None' },
      ...[...familyMap.values()]
        .map((label) => ({ label, value: label }))
        .sort((a, b) => {
          const [familyA] = a.label.split('-');
          const [familyB] = b.label.split('-');
          const rankDelta = hammerFamilyRank(familyA) - hammerFamilyRank(familyB);
          if (rankDelta !== 0) return rankDelta;
          return a.label.localeCompare(b.label);
        }),
    ];

    // DTH layout components consume options via useDSCSlot(slotId).options.<dropdownKey>,
    // so the bootstrap MUST store keys under '<slotId>:<dropdownKey>' form.
    return {
      'dth-bit:holeSize': dthBitOptions,
      'dth-bit:bitDiameter': dthBitOptions,
      'hammer-series:family': dthHammerFamilyOptions,
      'hammer-series:shank': [],
      'drillpipe:pinOption': [],
    };
  }

  return {};
}

/**
 * Convenience: builds both master lists AND initial options in one call.
 * Used by `DSCContext` on mount.
 */
export function buildDSCBootstrap(productLine, products, opts = {}) {
  const masters = buildDSCFilteringData(productLine, products, opts);
  const initialOptions = buildInitialSlotOptions(productLine, products, opts);
  return { ...masters, initialOptions };
}
